// Top-down tree layout for the workflow canvas. The graph is a small tree
// (one trigger, typically under a dozen steps), so a hand-written layout
// beats pulling in dagre: deterministic, parents centred over their children.

import { TRIGGER_ID, type ConditionEdge, type StepNode } from "./types.ts";

export const NODE_WIDTH = 240;
export const NODE_HEIGHT = 84;
export const X_GAP = 40;
export const Y_GAP = 72;

// Data-source pills hang to the left of an agent card (dataSources.ts); an
// agent reserves that gutter so sibling subtrees don't land on them.
export const DS_WIDTH = 170;
export const DS_HEIGHT = 44;
export const DS_GAP = 8;
export const DS_X_GAP = 56;
export const AGENT_LEFT_GUTTER = DS_WIDTH + DS_X_GAP;

export function layoutTree(nodes: StepNode[], edges: ConditionEdge[]): StepNode[] {
  const ids = new Set(nodes.map((n) => n.id));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const children = new Map<string, string[]>();
  const hasParent = new Set<string>();
  for (const e of edges) {
    if (!ids.has(e.source) || !ids.has(e.target)) continue;
    children.set(e.source, [...(children.get(e.source) ?? []), e.target]);
    hasParent.add(e.target);
  }

  // Trigger first, then orphaned subtrees in insertion order.
  const roots = nodes
    .filter((n) => !hasParent.has(n.id))
    .sort((a, b) => Number(b.id === TRIGGER_ID) - Number(a.id === TRIGGER_ID));

  const gutter = (id: string) => (byId.get(id)?.data.kind === "agent" ? AGENT_LEFT_GUTTER : 0);
  // The card plus, for an agent, the pills to its left.
  const ownWidth = (id: string) => NODE_WIDTH + gutter(id);

  const widthMemo = new Map<string, number>();
  const width = (id: string, trail: Set<string>): number => {
    const memo = widthMemo.get(id);
    if (memo !== undefined) return memo;
    if (trail.has(id)) return ownWidth(id); // cycle guard; should be unreachable
    const next = new Set(trail).add(id);
    const kids = (children.get(id) ?? []).filter((c) => !trail.has(c));
    const kidsWidth = kids.length
      ? kids.map((c) => width(c, next)).reduce((a, b) => a + b, 0) + X_GAP * (kids.length - 1)
      : 0;
    const w = Math.max(ownWidth(id), kidsWidth);
    widthMemo.set(id, w);
    return w;
  };

  const pos = new Map<string, { x: number; y: number }>();
  const place = (id: string, left: number, depth: number, trail: Set<string>) => {
    if (pos.has(id) || trail.has(id)) return;
    const w = width(id, trail);
    // Centre the card (and its gutter) in the subtree's span; the gutter sits left of the card.
    pos.set(id, {
      x: left + (w - ownWidth(id)) / 2 + gutter(id),
      y: depth * (NODE_HEIGHT + Y_GAP),
    });
    const next = new Set(trail).add(id);
    const kids = children.get(id) ?? [];
    if (!kids.length) return;
    const kidsWidth = kids.map((c) => width(c, next)).reduce((a, b) => a + b, 0) + X_GAP * (kids.length - 1);
    // Children centred under a parent wider than they are.
    let cursor = left + Math.max(0, (w - kidsWidth) / 2);
    for (const c of kids) {
      place(c, cursor, depth + 1, next);
      cursor += width(c, next) + X_GAP;
    }
  };

  let left = 0;
  for (const r of roots) {
    place(r.id, left, 0, new Set());
    left += width(r.id, new Set()) + X_GAP * 2;
  }

  return nodes.map((n) => ({ ...n, position: pos.get(n.id) ?? n.position }));
}
